import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import type { ConversationOutcome, ConversationStatus } from '@anchor-comms/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { useConversationsList } from './queries';
import { relativeTime } from '@/lib/utils';

const STATUSES: { value: ConversationStatus | 'all'; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];

const OUTCOMES: ConversationOutcome[] = ['won', 'lost', 'in_progress', 'abandoned'];

export function ConversationsListPage() {
  const [status, setStatus] = useState<ConversationStatus | 'all'>('active');
  const [outcome, setOutcome] = useState<ConversationOutcome | 'all'>('all');

  const queryParams = useMemo(
    () => ({
      ...(status !== 'all' ? { status } : {}),
      ...(outcome !== 'all' ? { outcome } : {}),
    }),
    [status, outcome],
  );

  const { data, isLoading, error } = useConversationsList(queryParams);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold">Conversations</h1>
        <Button asChild>
          <Link to="/conversations/new">New conversation</Link>
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1">
          {STATUSES.map((s) => (
            <Button
              key={s.value}
              size="sm"
              variant={status === s.value ? 'default' : 'outline'}
              onClick={() => setStatus(s.value)}
            >
              {s.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={outcome === 'all' ? 'default' : 'outline'}
            onClick={() => setOutcome('all')}
          >
            All outcomes
          </Button>
          {OUTCOMES.map((o) => (
            <Button
              key={o}
              size="sm"
              variant={outcome === o ? 'default' : 'outline'}
              onClick={() => setOutcome(o)}
            >
              {o.replace('_', ' ')}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}

      <div className="space-y-2">
        {data?.items.map((c) => (
          <Link key={c.id} to={`/conversations/${c.id}`} className="block">
            <Card className="p-4 hover:bg-accent transition-colors">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="space-y-1">
                  <div className="font-medium">
                    {c.customer.name}
                    {c.customer.company && (
                      <span className="text-muted-foreground"> · {c.customer.company}</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {c.topic.name} · {c.rep.name} · {c._count.messages} messages, {c._count.drafts} drafts
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant={c.status === 'active' ? 'default' : 'secondary'}>{c.status}</Badge>
                  {c.outcome && <Badge variant="outline">{c.outcome.replace('_', ' ')}</Badge>}
                  <span className="text-muted-foreground">{relativeTime(c.updatedAt)}</span>
                </div>
              </div>
            </Card>
          </Link>
        ))}
        {data && data.items.length === 0 && (
          <p className="text-muted-foreground">No conversations match these filters.</p>
        )}
      </div>
    </div>
  );
}
